/* eslint-disable dot-notation */
import { Grid } from '@mui/material';
import Divider from '@mui/material/Divider';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import { useEffect, useState } from 'react';

import { ReactComponent as Coin } from 'design_system/src/static/icons/coin.svg';
import { ReactComponent as Save } from 'design_system/src/static/icons/save.svg';

import { Button, Input, SideModal, Typography } from 'design_system/src';

import { editVehicle } from 'models/inventory/controllers/vehicle.controller';
import {
	CreateVehicleDto,
	UpdateVehicleDto,
	VehiclePopulated,
} from 'models/inventory/interfaces/vehicle.interface';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { priceFormatFinancial } from 'models/shared/helpers/prices';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { filterObjectByKeys } from 'models/shared/helpers/utils';
import {
	vehiclePricingDefaults,
	vehiclePricingValidationSchema,
} from './constants';

interface EditProps {
	open: boolean;
	setOpen: Function;
	values?: Partial<VehiclePopulated> | null;
	setResult: Function;
}

function EdiVehiclePricing({
	open,
	setOpen,
	values = null,
	setResult,
}: EditProps) {
	const [initial, setInitial] = useState<UpdateVehicleDto | CreateVehicleDto>(
		vehiclePricingDefaults,
	);
	const [showErrors, setShowErrors] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		let initialValue: UpdateVehicleDto | CreateVehicleDto =
			vehiclePricingDefaults;
		if (values?._id) {
			initialValue = filterObjectByKeys(
				values,
				Object.keys(vehiclePricingDefaults) as any,
			);
		}
		setInitial(initialValue);
	}, [values]);

	const displayFormErrors = (form: any) => {
		if (Object.keys(form.errors)?.length)
			toasterNotification({
				msg: `Hay errores en los campos: ${Object.keys(form.errors).reduce(
					(acc, ele) => `${acc ? `${acc}, ` : ''}'${ele}'`,
					'',
				)}`,
				style: 'light',
				toasterType: toasterOptions.error,
			});
		setShowErrors(true);
	};

	const displayRequestErrors = (err: any) => {
		let msg = 'Ocurrio un error';
		if (typeof err === 'string') msg = err;
		else if (typeof err.message === 'string') msg = err.message;
		toasterNotification({
			msg,
			toasterType: toasterOptions.error,
			style: 'light',
		});
		setLoading(false);
	};

	const onSubmit = (
		vals: UpdateVehicleDto | CreateVehicleDto,
		_form: FormikHelpers<UpdateVehicleDto | CreateVehicleDto>,
	) => {
		setLoading(true);
		if (values?._id) {
			editVehicle(vals as UpdateVehicleDto, values.internalId ?? '')
				.then((ele) => {
					setLoading(false);
					setOpen(false);
					setResult(ele);
				})
				.catch(displayRequestErrors);
		}
	};

	const form = useFormik<UpdateVehicleDto | CreateVehicleDto>({
		initialValues: initial,
		validationSchema: vehiclePricingValidationSchema,
		onSubmit,
		enableReinitialize: true,
		validateOnMount: true,
	});

	const totalCost =
		(form.values.purchaseCost || 0) +
		(form.values.extraCosts?.basicExtraCost || 0) +
		(form.values.extraCosts?.gpsHardwareCost || 0) +
		(form.values.extraCosts?.tileHardwareCost || 0);

	return (
		initial && (
			<SideModal open={open} setOpen={setOpen} size="medium">
				<FormikProvider value={form}>
					<div className="flex_header">
						<Typography
							scale="heading4"
							weight="600"
							textColor="neutral_900"
							className="m_b_lg m_t_md"
						>
							Editar precios
						</Typography>
						<Divider />
					</div>
					<div className="flex_body p_y_lg p_x_sm overflow_x_hidden">
						<Grid container direction="row" spacing={3}>
							<Grid item xs={12}>
								<Typography
									scale="large"
									weight="600"
									textColor="neutral_700"
									className="m_b_lg"
								>
									Costo del vehículo
								</Typography>
							</Grid>
							<Grid item xs={6}>
								<Input
									onBlur={form.handleBlur}
									error={
										!!form.errors.purchaseCost &&
										(form.getFieldMeta('purchaseCost').touched || showErrors)
									}
									subtitle={
										form.getFieldMeta('purchaseCost').touched || showErrors
											? (form.errors.purchaseCost as string)
											: undefined
									}
									title="Costo de compra"
									placeholder="Costo del vehículo"
									name="purchaseCost"
									type="number"
									min={0}
									icon={<Coin />}
									className="m_b_xs w_100_per"
									value={form.values.purchaseCost}
									onChange={form.handleChange}
								/>
							</Grid>
							<Grid item xs={6}>
								<Input
									onBlur={form.handleBlur}
									error={
										!!form.errors.extraCosts &&
										(form.getFieldMeta('extraCosts.basicExtraCost').touched ||
											showErrors)
									}
									subtitle={
										form.getFieldMeta('extraCosts.basicExtraCost').touched ||
										showErrors
											? _.get(form.errors, 'extraCosts.basicExtraCost', '')
											: undefined
									}
									title="Gastos extras"
									placeholder="Ejemplo: GPS, Sirenas, Tracker etc."
									name="extraCosts.basicExtraCost"
									type="number"
									min={0}
									icon={<Coin />}
									className="m_b_xs w_100_per"
									value={form.values.extraCosts?.basicExtraCost}
									onChange={form.handleChange}
								/>
							</Grid>
							<Grid item xs={6}>
								<Input
									onBlur={form.handleBlur}
									error={
										!!form.errors.extraCosts &&
										(form.getFieldMeta('extraCosts.tileHardwareCost').touched ||
											showErrors)
									}
									subtitle={
										form.getFieldMeta('extraCosts.tileHardwareCost').touched ||
										showErrors
											? _.get(form.errors, 'extraCosts.tileHardwareCost', '')
											: undefined
									}
									title="Costo Hardware Tile"
									placeholder="Costo del hardware"
									name="extraCosts.tileHardwareCost"
									type="number"
									min={0}
									icon={<Coin />}
									className="m_b_xs w_100_per"
									value={form.values.extraCosts?.tileHardwareCost}
									onChange={form.handleChange}
								/>
							</Grid>
							<Grid item xs={6}>
								<Input
									onBlur={form.handleBlur}
									error={
										!!form.errors.extraCosts &&
										(form.getFieldMeta('extraCosts.gpsHardwareCost').touched ||
											showErrors)
									}
									subtitle={
										form.getFieldMeta('extraCosts.gpsHardwareCost').touched ||
										showErrors
											? _.get(form.errors, 'extraCosts.gpsHardwareCost', '')
											: undefined
									}
									title="Costo Hardware GPS"
									placeholder="Costo del hardware"
									name="extraCosts.gpsHardwareCost"
									type="number"
									min={0}
									icon={<Coin />}
									className="m_b_xs w_100_per"
									value={form.values.extraCosts?.gpsHardwareCost}
									onChange={form.handleChange}
								/>
							</Grid>
							<Grid item xs={6}>
								<Input
									onBlur={form.handleBlur}
									error={
										!!form.errors.extraCosts &&
										(form.getFieldMeta('extraCosts.gpsMonthlySoftwareCost')
											.touched ||
											showErrors)
									}
									subtitle={
										form.getFieldMeta('extraCosts.gpsMonthlySoftwareCost')
											.touched || showErrors
											? _.get(
													form.errors,
													'extraCosts.gpsMonthlySoftwareCost',
													'',
											  )
											: undefined
									}
									title="Costo Mensual GPS"
									placeholder="Plataforma GPS"
									name="extraCosts.gpsMonthlySoftwareCost"
									type="number"
									min={0}
									icon={<Coin />}
									className="m_b_xs w_100_per"
									value={form.values.extraCosts?.gpsMonthlySoftwareCost}
									onChange={form.handleChange}
								/>
							</Grid>
							<Grid item xs={12}>
								<Divider />
								<div className="display_flex flex_justify_end flex_gap_lg m_y_md flex_align_center">
									<Typography
										scale="small"
										weight="600"
										textColor="neutral_900"
									>
										Costo total de la moto:
									</Typography>
									<Typography
										scale="heading3"
										weight="600"
										textColor="primary_300"
									>
										{priceFormatFinancial(totalCost, 0)}
									</Typography>
								</div>
								<Typography
									scale="xsmall"
									weight="600"
									textColor="neutral_600"
									className="w_100_per text_right"
								>
									*No se incluye el gasto mensual del software del GPS
								</Typography>
							</Grid>
						</Grid>
					</div>
					<div className="flex_footer">
						<Button
							scale="small"
							orientation="right"
							icon={<Save />}
							className="m_b_xs w_100_per"
							type="submit"
							onClick={() => {
								displayFormErrors(form);
								form.handleSubmit();
							}}
							disabled={loading || !form.dirty}
						>
							Guardar
						</Button>
					</div>
				</FormikProvider>
			</SideModal>
		)
	);
}

export default EdiVehiclePricing;
